import { useEffect, useRef, useState } from 'react'
import { useAuth } from '../../contexts/AuthContext'

const GoogleSignInButton = () => {
  const buttonRef = useRef(null)
  const [isGoogleLoaded, setIsGoogleLoaded] = useState(false)
  const [renderError, setRenderError] = useState(false)
  const hasRenderedRef = useRef(false)
  const { signInWithGoogle } = useAuth()

  // Listen for Google API being loaded globally
  useEffect(() => {
    // Define handler for when Google API loads
    const handleGoogleLoad = () => {
      setIsGoogleLoaded(true)
    }

    // Add event listener if we're in a browser environment and Google is not yet available
    if (
      typeof window !== 'undefined' &&
      (!window.google || !window.google.accounts)
    ) {
      window.addEventListener('google-loaded', handleGoogleLoad)
    } else if (
      typeof window !== 'undefined' &&
      window.google &&
      window.google.accounts
    ) {
      setIsGoogleLoaded(true)
    }

    // Set a timeout to show fallback if Google doesn't load in 3 seconds
    const timeoutId = setTimeout(() => {
      if (!isGoogleLoaded) {
        setRenderError(true)
      }
    }, 3000)

    return () => {
      if (typeof window !== 'undefined') {
        window.removeEventListener('google-loaded', handleGoogleLoad)
        clearTimeout(timeoutId)
      }
    }
  }, [isGoogleLoaded])

  // Render the button when Google is loaded and only once
  useEffect(() => {
    if (buttonRef.current && isGoogleLoaded && !hasRenderedRef.current) {
      if (
        window.google &&
        window.google.accounts &&
        window.google.accounts.id
      ) {
        try {
          // Clear any existing content
          buttonRef.current.innerHTML = ''

          // Render the button
          window.google.accounts.id.renderButton(buttonRef.current, {
            type: 'standard',
            theme: 'outline',
            text: 'signin_with',
            shape: 'pill',
            size: 'large',
            locale: 'en_US',
          })

          // Mark as rendered
          hasRenderedRef.current = true
          setRenderError(false)
        } catch (error) {
          console.error('Failed to render Google button:', error)
          setRenderError(true)
        }
      } else {
        setRenderError(true)
      }
    }
  }, [isGoogleLoaded])

  // Fallback button for when Google button fails to render
  if (renderError) {
    return (
      <button
        onClick={signInWithGoogle}
        className="flex items-center justify-center gap-2 px-4 py-2 border border-gray-300 rounded-full text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
        style={{
          height: '40px',
          minWidth: '240px',
        }}
      >
        <svg viewBox="0 0 48 48" width="24px" height="24px">
          <path
            fill="#FFC107"
            d="M43.611,20.083H42V20H24v8h11.303c-1.649,4.657-6.08,8-11.303,8c-6.627,0-12-5.373-12-12c0-6.627,5.373-12,12-12c3.059,0,5.842,1.154,7.961,3.039l5.657-5.657C34.046,6.053,29.268,4,24,4C12.955,4,4,12.955,4,24c0,11.045,8.955,20,20,20c11.045,0,20-8.955,20-20C44,22.659,43.862,21.35,43.611,20.083z"
          />
          <path
            fill="#FF3D00"
            d="M6.306,14.691l6.571,4.819C14.655,15.108,18.961,12,24,12c3.059,0,5.842,1.154,7.961,3.039l5.657-5.657C34.046,6.053,29.268,4,24,4C16.318,4,9.656,8.337,6.306,14.691z"
          />
          <path
            fill="#4CAF50"
            d="M24,44c5.166,0,9.86-1.977,13.409-5.192l-6.19-5.238C29.211,35.091,26.715,36,24,36c-5.202,0-9.619-3.317-11.283-7.946l-6.522,5.025C9.505,39.556,16.227,44,24,44z"
          />
          <path
            fill="#1976D2"
            d="M43.611,20.083H42V20H24v8h11.303c-0.792,2.237-2.231,4.166-4.087,5.571c0.001-0.001,0.002-0.001,0.003-0.002l6.19,5.238C36.971,39.205,44,34,44,24C44,22.659,43.862,21.35,43.611,20.083z"
          />
        </svg>
        Sign in with Google
      </button>
    )
  }

  return (
    <div
      ref={buttonRef}
      className="google-signin-button mx-auto"
      style={{
        height: '40px',
        minWidth: '240px',
        display: 'flex',
        justifyContent: 'center',
      }}
    ></div>
  )
}

export default GoogleSignInButton
